import { uniqueId } from 'lodash-es';
import { GlAlert, GlForm, GlFormGroup, GlModal, GlSprintf } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { __, s__, sprintf } from '~/locale';
import { TYPENAME_PROJECT, TYPENAME_GROUP } from '~/graphql_shared/constants';
import { convertToGraphQLId, getIdFromGraphQLId } from '~/graphql_shared/utils';
import { joinPaths } from '~/lib/utils/url_utility';
import { FLOW_TRIGGER_TYPE_MENTION, FLOW_TRIGGER_TYPE_ASSIGN, FLOW_TRIGGER_TYPE_ASSIGN_REVIEWER } from 'ee/ai/duo_agents_platform/constants';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import FlowTriggerConditions from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/duo_agents_platform/pages/flow_triggers/components/flow_trigger_conditions.vue?vue3';
import { denormalizeMergeRequestEventTypes, eventTypeIntsToValues, eventTypeValuesToInts } from 'ee/ai/duo_agents_platform/utils';
import { AI_CATALOG_TYPE_FLOW, AI_CATALOG_TYPE_THIRD_PARTY_FLOW, AI_CATALOG_PROJECT_CONSUMER_LABEL_DESCRIPTION, AI_CATALOG_PROJECT_MULTI_CONSUMER_LABEL_DESCRIPTION, AI_CATALOG_ITEM_CONSUMER_DISCLAIMER_TEXTS, MAX_PROJECTS_BULK_ENABLE, VISIBILITY_LEVEL_PUBLIC, VISIBILITY_LEVEL_PRIVATE, VISIBILITY_LEVEL_RESTRICTED } from '../constants';
import { getRegistryItem } from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/item_type_registry.js?vue3';
import { getAiCatalogItemOwningProjectName } from '../capabilities';
import FormProjectMultiSelect from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_project_multi_select.vue?vue3';
import FormProjectDropdown from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_project_dropdown.vue?vue3';
import AiCatalogItemConsumerDisclaimer from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/ai_catalog_item_consumer_disclaimer.vue?vue3';
const DEFAULT_TRIGGER_TYPES = [
    FLOW_TRIGGER_TYPE_MENTION.value,
    FLOW_TRIGGER_TYPE_ASSIGN.value,
    FLOW_TRIGGER_TYPE_ASSIGN_REVIEWER.value
];
export default {
    name: 'AiCatalogItemConsumerModal',
    formId: uniqueId('ai-catalog-item-consumer-form-'),
    components: {
        FormProjectMultiSelect,
        FormProjectDropdown,
        FlowTriggerConditions,
        GlAlert,
        GlForm,
        GlFormGroup,
        GlModal,
        GlSprintf,
        AiCatalogItemConsumerDisclaimer
    },
    mixins: [
        glFeatureFlagsMixin()
    ],
    inject: {
        isGlobalNamespace: {},
        isProjectNamespace: {},
        isGroupNamespace: {},
        contextProjectId: {
            from: 'projectId',
            default: null
        },
        groupId: {
            default: null
        }
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        open: {
            type: Boolean,
            required: false,
            default: false
        },
        modalId: {
            type: String,
            required: false,
            default: 'add-item-consumer-modal'
        },
        canEnable: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'hide',
        'submit'
    ],
    data () {
        return {
            isOpen: this.open,
            projectId: null,
            projectIds: [],
            isDirty: false,
            error: null,
            triggerTypes: [],
            triggerGoals: {},
            triggerFilter: {},
            areTriggersValid: true,
            mountedAt: null
        };
    },
    computed: {
        itemRegistry () {
            return getRegistryItem(this.item.itemType);
        },
        defaultProjectId () {
            if (this.isProjectNamespace) {
                return convertToGraphQLId(TYPENAME_PROJECT, this.contextProjectId);
            }
            return this.item.visibility === VISIBILITY_LEVEL_PUBLIC ? null : this.item.project?.id;
        },
        showProjectSection () {
            return this.isGlobalNamespace;
        },
        isGroupPrivate () {
            return this.isGroupNamespace && this.isPrivate;
        },
        itemEditPath () {
            if (!this.item.project?.webPath) return null;
            return joinPaths(this.item.project.webPath, this.itemRegistry.namespacePath, getIdFromGraphQLId(this.item.id).toString(), 'edit');
        },
        modal () {
            if (this.isGroupPrivate) {
                return {
                    actionPrimary: {
                        text: sprintf(s__('AICatalog|Go to %{itemType} settings'), {
                            itemType: this.itemTypeLabel
                        }),
                        attributes: {
                            variant: 'confirm',
                            href: this.itemEditPath
                        }
                    },
                    actionCancel: null
                };
            }
            return {
                actionPrimary: {
                    text: this.isGroupNamespace ? __('Enable for all projects') : __('Enable'),
                    attributes: {
                        variant: 'confirm',
                        type: 'submit',
                        form: this.$options.formId,
                        disabled: !this.canEnable
                    }
                },
                actionCancel: {
                    text: __('Cancel')
                }
            };
        },
        itemTypeLabel () {
            return this.itemRegistry.label;
        },
        title () {
            if (this.isGroupPrivate) {
                return sprintf(s__('AICatalog|This %{itemType} is private'), {
                    itemType: this.itemTypeLabel
                });
            }
            if (this.isGroupNamespace) {
                return sprintf(s__('AICatalog|Enable %{itemType} in your group'), {
                    itemType: this.itemTypeLabel
                });
            }
            return sprintf(s__('AICatalog|Enable %{itemType} in your project'), {
                itemType: this.itemTypeLabel
            });
        },
        isRestricted () {
            return this.item.visibility === VISIBILITY_LEVEL_RESTRICTED;
        },
        isPrivate () {
            return this.item.visibility === VISIBILITY_LEVEL_PRIVATE;
        },
        restrictedGroupName () {
            return this.item.project?.rootGroup?.fullName;
        },
        owningProjectName () {
            return getAiCatalogItemOwningProjectName(this.item);
        },
        canEnableOnMultipleProjects () {
            return this.item.visibility === VISIBILITY_LEVEL_PUBLIC && this.isGlobalNamespace;
        },
        projectLabelDescription () {
            return this.canEnableOnMultipleProjects ? AI_CATALOG_PROJECT_MULTI_CONSUMER_LABEL_DESCRIPTION[this.item.itemType] : AI_CATALOG_PROJECT_CONSUMER_LABEL_DESCRIPTION[this.item.itemType];
        },
        isProjectValid () {
            if (!this.isDirty) return true;
            return this.canEnableOnMultipleProjects ? this.projectIds.length > 0 && this.projectIds.length <= MAX_PROJECTS_BULK_ENABLE : Boolean(this.projectId);
        },
        projectInvalidFeedback () {
            if (!this.canEnableOnMultipleProjects) {
                return s__('AICatalog|Project is required.');
            }
            if (this.projectIds.length > MAX_PROJECTS_BULK_ENABLE) {
                return s__('AICatalog|Limit of 100 projects reached.');
            }
            return s__('AICatalog|At least one project is required.');
        },
        showStaticFields () {
            return this.isPrivate;
        },
        isFormValid () {
            if (this.isGroupNamespace) {
                return this.hasValidTriggers;
            }
            return this.isProjectValid && this.hasValidTriggers;
        },
        isFoundationalFlow () {
            return this.item.itemType === AI_CATALOG_TYPE_FLOW && this.item.foundational;
        },
        showFlowInheritanceDisclaimer () {
            return this.isGroupNamespace && this.item.itemType === AI_CATALOG_TYPE_FLOW;
        },
        flowInheritanceDisclaimer () {
            return AI_CATALOG_ITEM_CONSUMER_DISCLAIMER_TEXTS[AI_CATALOG_TYPE_FLOW].groupEnabledForMembers;
        },
        showTriggers () {
            if (this.isFoundationalFlow) {
                return false;
            }
            return [
                AI_CATALOG_TYPE_FLOW,
                AI_CATALOG_TYPE_THIRD_PARTY_FLOW
            ].includes(this.item.itemType);
        },
        goalSupported () {
            return Boolean(this.glFeatures.aiFlowTriggerGoals && this.item.acceptsTriggerGoal);
        },
        hasValidTriggers () {
            return !this.showTriggers || this.areTriggersValid;
        }
    },
    watch: {
        defaultProjectId (newVal) {
            if (this.projectId == null && newVal) {
                this.projectId = newVal;
                Sentry.captureException(new Error('AiCatalogItemConsumerModal: project data arrived after mount'), {
                    level: 'info',
                    tags: {
                        race_type: 'ai_catalog_project_field',
                        item_type: this.item?.itemType
                    },
                    extra: {
                        item_id: this.item?.id,
                        time_to_resolve_ms: this.mountedAt != null ? Math.round(performance.now() - this.mountedAt) : null
                    }
                });
            }
        }
    },
    mounted () {
        this.mountedAt = performance.now();
        this.resetForm();
    },
    methods: {
        onError (error) {
            this.error = error;
        },
        handleSubmit () {
            this.isDirty = true;
            if (!this.isFormValid) {
                return;
            }
            this.isOpen = false;
            this.isDirty = false;
            let payload;
            if (this.canEnableOnMultipleProjects) {
                payload = {
                    target: this.projectIds
                };
            } else if (this.isGroupNamespace) {
                payload = {
                    target: {
                        groupId: convertToGraphQLId(TYPENAME_GROUP, this.groupId)
                    }
                };
            } else {
                payload = {
                    target: {
                        projectId: this.projectId
                    }
                };
            }
            if (this.showTriggers) {
                // The field consolidates merge_request_ready (4) and merge_request_code_conflict (5)
                // into merge_request (6) with an action filter. The backend dispatches ready/conflict
                // via separate event types, so split them back out before submitting.
                const { eventTypes, filter, goals } = denormalizeMergeRequestEventTypes({
                    eventTypes: eventTypeValuesToInts(this.triggerTypes),
                    filter: this.triggerFilter,
                    goals: this.triggerGoals
                });
                payload.triggerTypes = eventTypeIntsToValues(eventTypes);
                if (Object.keys(filter).length > 0) {
                    payload.triggerFilter = filter;
                }
                if (Object.keys(goals).length > 0) {
                    payload.triggerGoals = goals;
                }
            }
            this.$emit('submit', payload);
        },
        resetForm () {
            this.projectId = this.defaultProjectId;
            this.projectIds = [];
            this.isDirty = false;
            this.error = null;
            this.triggerTypes = [
                ...DEFAULT_TRIGGER_TYPES
            ];
            this.triggerFilter = {};
            this.triggerGoals = {};
            this.areTriggersValid = true;
        },
        onHidden () {
            this.resetForm();
            this.$emit('hide');
        }
    }
};
